import { get, set } from 'idb-keyval';

/**
 * v5 — Paramètres d'adaptation du média de profil
 *
 * Réglages PAR MÉDIA séparément :
 * - contain : garde proportions, peut laisser des bandes noires
 * - cover : remplit tout, peut rogner
 * - blur-bg : image floutée en fond + image originale par-dessus
 *
 * + Zoom (50% à 200%) + Position (3×3)
 *
 * Stockage : dictionnaire keyed par signature du média + "default" pour
 * les nouveaux médias qui n'ont pas encore de réglages.
 */

export type FitMode = 'contain' | 'cover' | 'blur-bg';
export type HPos = 'left' | 'center' | 'right';
export type VPos = 'top' | 'middle' | 'bottom';

export interface MediaFitConfig {
  fitMode: FitMode;
  zoom: number;            // 50 → 200
  hPos: HPos;
  vPos: VPos;
  blurAmount: number;      // 0 → 40
  blurOpacity: number;     // 30 → 100
}

export interface ProfileMediaFitMap {
  default: MediaFitConfig;
  [mediaKey: string]: MediaFitConfig;
}

const STORAGE_KEY = 'profileMediaFitSettings';

export const DEFAULT_MEDIA_FIT: MediaFitConfig = {
  fitMode: 'blur-bg',
  zoom: 100,
  hPos: 'center',
  vPos: 'middle',
  blurAmount: 20,
  blurOpacity: 80,
};

export const DEFAULT_PROFILE_FIT_MAP: ProfileMediaFitMap = {
  default: DEFAULT_MEDIA_FIT,
};

export async function getProfileMediaFitMap(): Promise<ProfileMediaFitMap> {
  try {
    const stored = await get<any>(STORAGE_KEY);
    if (stored && typeof stored === 'object') {
      // Migration depuis ancien format (un seul objet plat sans "default")
      if ('fitMode' in stored && 'zoom' in stored && !stored.default) {
        const migrated: ProfileMediaFitMap = {
          default: { ...DEFAULT_MEDIA_FIT, ...stored },
        };
        await set(STORAGE_KEY, migrated);
        return migrated;
      }
      if (stored.default) {
        return { ...DEFAULT_PROFILE_FIT_MAP, ...stored };
      }
    }
  } catch (e) {
    console.error('[ProfileFit] load error', e);
  }
  return DEFAULT_PROFILE_FIT_MAP;
}

export async function saveProfileMediaFitMap(map: ProfileMediaFitMap): Promise<boolean> {
  try {
    await set(STORAGE_KEY, map);
    window.dispatchEvent(
      new CustomEvent('profileMediaFitUpdated', { detail: map })
    );
    return true;
  } catch (e) {
    console.error('[ProfileFit] save error', e);
    return false;
  }
}

export function getConfigForMedia(map: ProfileMediaFitMap, mediaUrl: string): MediaFitConfig {
  if (!mediaUrl) return map.default;
  const key = getMediaKey(mediaUrl);
  return map[key] || map.default;
}

export function getMediaKey(mediaUrl: string): string {
  if (!mediaUrl) return 'default';
  if (mediaUrl.startsWith('data:')) {
    if (mediaUrl.length <= 80) return mediaUrl;
    return mediaUrl.slice(0, 50) + '...' + mediaUrl.slice(-30);
  }
  return mediaUrl;
}

export function updateMediaConfig(
  map: ProfileMediaFitMap,
  mediaUrl: string | null,
  newConfig: MediaFitConfig
): ProfileMediaFitMap {
  const key = mediaUrl ? getMediaKey(mediaUrl) : 'default';
  return {
    ...map,
    [key]: newConfig,
  };
}

const H_POS_MAP: Record<HPos, string> = {
  left: '0%',
  center: '50%',
  right: '100%',
};
const V_POS_MAP: Record<VPos, string> = {
  top: '0%',
  middle: '50%',
  bottom: '100%',
};

export function getProfileMediaStyles(fit: MediaFitConfig): React.CSSProperties {
  const scale = fit.zoom / 100;
  const objectPosition = `${H_POS_MAP[fit.hPos]} ${V_POS_MAP[fit.vPos]}`;

  const base: React.CSSProperties = {
    width: '100%',
    height: '100%',
    transform: scale !== 1 ? `scale(${scale})` : undefined,
    transformOrigin: 'center',
    objectPosition,
  };

  if (fit.fitMode === 'contain') {
    return { ...base, objectFit: 'contain' };
  } else if (fit.fitMode === 'cover') {
    return { ...base, objectFit: 'cover' };
  } else {
    return { ...base, objectFit: 'contain', position: 'relative', zIndex: 1 };
  }
}

export function getBlurBgStyles(fit: MediaFitConfig): React.CSSProperties {
  if (fit.fitMode !== 'blur-bg') return { display: 'none' };
  return {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    filter: `blur(${fit.blurAmount}px)`,
    transform: 'scale(1.15)',
    opacity: fit.blurOpacity / 100,
    zIndex: 0,
    pointerEvents: 'none',
  };
}

/* Compat ancienne API */
export type ProfileMediaFitSettings = MediaFitConfig;
export const DEFAULT_PROFILE_FIT = DEFAULT_MEDIA_FIT;

export async function getProfileMediaFit(): Promise<MediaFitConfig> {
  const map = await getProfileMediaFitMap();
  return map.default;
}

export async function saveProfileMediaFit(s: MediaFitConfig): Promise<boolean> {
  const map = await getProfileMediaFitMap();
  return saveProfileMediaFitMap({ ...map, default: s });
}
